import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Button, IconButton, DSProvider, Tooltip } from "@toddle-edu/ds-web";
import { DeleteOutlined } from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import {
  mapStateToProps,
  mapActionCreators,
  getIsRecurringEventSettingsUpdated,
  setValue,
  isInvalidVideoMeetingForm,
  getVideoMeetingDetails,
} from "../../modules/Utils";
import { connect } from "react-redux";
import EventUpdateConfirmationModal from "../EventUpdateConfirmationModal";
import RecurrenceConfirmationModal from "../RecurrenceConfirmationModal";
import {
  createVideoMeeting,
  deleteSchoolCalendarEvent,
} from "MyCalendar/modules/MyCalendarModule";
import useDeleteRecurrance from "../../hooks/useDeleteRecurrance";
import {
  useIsOnlyRemindersUpdated,
  useIsVideoMeetingFormUpdated,
  useUpdateRecurrance,
} from "../../hooks";
import { getDateMomentObject } from "Utils/DateTimeHelpers";
import { CREATE } from "Constants/stringConstants";
import SpinLoader from "../SpinLoader/SpinLoader";
import { editVideoMeeting } from "MyCalendar/modules/MyCalendarModule/MyCalendarThunks";

const localMapActionCreators = {
  ...mapActionCreators,
  createVideoMeeting,
  deleteSchoolCalendarEvent,
  editVideoMeeting,
};

const spinLoaderContainerStyle = {
  minWidth: 100,
  textAlign: "center",
};
const CreateButton = ({
  onClick,
  testId = "create-event-button",
  eventModalV2,
  label = null,
  createVideoMeeting,
  updateEventModalV2,
}) => {
  const { t } = useI18n(["common", "calendar"]);
  const isFormInvalid = isInvalidVideoMeetingForm(eventModalV2);
  const isFormUpdated = useIsVideoMeetingFormUpdated();
  const setIsVideoMeetingFormUpdated = setValue(
    "isVideoMeetingFormUpdated",
    updateEventModalV2
  );
  const [
    isVideoMeetingCreationLoading,
    setIsVideoMeetingCreationLoading,
  ] = useState(false);

  useEffect(() => {
    setIsVideoMeetingFormUpdated(isFormUpdated);
  }, [isFormUpdated]);

  const clickHandler = async () => {
    setIsVideoMeetingCreationLoading(true);
    const meetingDetails = getVideoMeetingDetails(eventModalV2);
    try {
      await createVideoMeeting({ ...meetingDetails, action: CREATE });
      if (_.isFunction(onClick)) {
        onClick();
      }
    } catch (error) {
      console.error("Error creating event:", error);
    } finally {
      setIsVideoMeetingCreationLoading(false);
    }
  };
  const buttonJSX = (
    <Button
      variant="primary"
      onClick={isVideoMeetingCreationLoading ? _.noop : clickHandler}
      testId={testId}
      disabled={isFormInvalid}
    >
      <SpinLoader
        isLoading={isVideoMeetingCreationLoading}
        containerStyle={spinLoaderContainerStyle}
      >
        {label ? label : t("common:create")}
      </SpinLoader>
    </Button>
  );

  return isFormInvalid ? (
    <Tooltip
      dsVersion="2.0"
      tooltip={t("calendar:fill_mandatory_fields")}
      placement="bottomLeft"
      showArrow={true}
    >
      {buttonJSX}
    </Tooltip>
  ) : (
    buttonJSX
  );
};

export const CreateVideoMeetingButton = connect(
  mapStateToProps,
  localMapActionCreators
)(CreateButton);

const UpdateButton = ({
  onClick,
  testId = "update-event-button",
  eventModalV2,
  label = null,
  editVideoMeeting,
  setShowFullScreenLoader,
  updateEventModalV2,
}) => {
  const isFormInvalid = isInvalidVideoMeetingForm(eventModalV2);
  const isOnlyRemindersUpdated = useIsOnlyRemindersUpdated();
  const {
    clickHandler,
    isConfirmationModalOpen,
    setIsConfirmationModalOpen,
    onSaveRecurrenceHandler,
    repeatEventModalActionRef,
    existingLabelText,
    updatedLabelText,
    showNewConfirmationModal,
    t,
    onCloseConfirmationModal,
    isRemoveRepeatRecurranceSettings,
  } = useUpdateRecurrance({ updateEventModalV2, eventModalV2 });

  const {
    readOnlyExistingRepeatEventSettings,
    startDate,
    repeatEventSettings,
    readOnlyStartDate,
    readOnlyEndDate,
    endDate,
    isRepeatEvent,
    readOnlySkipNonOperationalDays,
    skipNonOperationalDays,
  } = eventModalV2 || {};

  const isFormUpdated = useIsVideoMeetingFormUpdated();
  const setIsVideoMeetingFormUpdated = setValue(
    "isVideoMeetingFormUpdated",
    updateEventModalV2
  );
  useEffect(() => {
    // Q. why we update the isFormUpdated state in the Redux store whenever it changes
    // A. This ensures that other components (useEventModal) relying on this state have the most current information
    setIsVideoMeetingFormUpdated(isFormUpdated);
  }, [isFormUpdated]);
  const isRecurringEventSettingsUpdated = isRepeatEvent
    ? getIsRecurringEventSettingsUpdated(
        readOnlyExistingRepeatEventSettings,
        repeatEventSettings,
        startDate
      )
    : false;
  const isSkipNonOperationalDaysUpdated = isRepeatEvent
    ? readOnlySkipNonOperationalDays !== skipNonOperationalDays
    : false;
  const isDateChanged =
    !_.isEqual(
      getDateMomentObject(readOnlyStartDate).format("YYYY-MM-DD"),
      getDateMomentObject(startDate).format("YYYY-MM-DD")
    ) ||
    !_.isEqual(
      getDateMomentObject(readOnlyEndDate).format("YYYY-MM-DD"),
      getDateMomentObject(endDate).format("YYYY-MM-DD")
    );

  const onConfirm = async data => {
    const { message } = data;
    const videoMeetingDetails = getVideoMeetingDetails(eventModalV2, message);
    try {
      setShowFullScreenLoader(true);
      await editVideoMeeting(videoMeetingDetails);
      if (_.isFunction(onClick)) {
        onClick(data);
      }
    } catch (error) {
      console.error("Error creating event:", error);
    }
    setIsConfirmationModalOpen(false);
    setShowFullScreenLoader(false);
  };

  return (
    <>
      <EventUpdateConfirmationModal
        isOpen={isConfirmationModalOpen}
        onConfirm={onConfirm}
        onClose={onCloseConfirmationModal}
        height="max-content"
        showWarning={!showNewConfirmationModal}
        saveButtonLabel="common:update"
        messagePlaceholder="calendar:add_optional_message"
        showMessageLabel={!showNewConfirmationModal}
        showCheckbox={!isOnlyRemindersUpdated}
        showMessageInput={!isOnlyRemindersUpdated}
        confirmationMessage={
          "calendar:update_video_meeting_confirmation_message"
        }
        title="calendar:update_video_meeting"
      />

      <RecurrenceConfirmationModal
        actionRef={repeatEventModalActionRef}
        isOpen={false}
        eventTitle={eventModalV2?.eventTitle || ""}
        existingLabelText={existingLabelText}
        updatedLabelText={
          isRemoveRepeatRecurranceSettings
            ? t("calendar:meeting_does_not_repeat")
            : updatedLabelText
        }
        title={t("calendar:edit_repeat_meeting")}
        showRepeatInfo={!_.isEqual(existingLabelText, updatedLabelText)}
        onSave={onSaveRecurrenceHandler}
        onClose={onCloseConfirmationModal}
        isRemoveRepeatRecurranceSettings={isRemoveRepeatRecurranceSettings}
        isDateChanged={isDateChanged}
        isRecurringEventSettingsUpdated={
          isRecurringEventSettingsUpdated || isSkipNonOperationalDaysUpdated
        }
        thisLabelText="calendar:edit_this_meeting_only"
        thisAndAllFututreLabelText="calendar:edit_this_and_all_future_meetings"
        allEventsLabelText="calendar:edit_all_meetings"
      />
      <Button
        variant="primary"
        onClick={clickHandler}
        testId={testId}
        disabled={isFormInvalid || !isFormUpdated}
      >
        {label ? label : t("common:save")}
      </Button>
    </>
  );
};

export const UpdateVideoMeetingButton = connect(
  mapStateToProps,
  localMapActionCreators
)(UpdateButton);

const DeleteButton = ({
  onClick,
  testId = "update-event-button",
  eventModalV2,
  style = {},
  deleteSchoolCalendarEvent,
  isFullDayEvent,
  id,
  onCloseEventDetailsModal,
  updateEventModalV2,
  isRepeatEvent,
}) => {
  const {
    clickHandler,
    isConfirmationModalOpen,
    onCloseConfirmationModal,
    onSaveRecurrenceHandler,
    repeatEventModalActionRef,
    setIsConfirmationModalOpen,
    t,
  } = useDeleteRecurrance({ updateEventModalV2, isRepeatEvent });

  const onConfirm = async data => {
    const { message, sendMessage } = data;
    try {
      await deleteSchoolCalendarEvent({
        id,
        isFullDayEvent,
        message,
        sendMessage,
      });
      if (_.isFunction(onClick)) {
        onClick(data);
      }
    } catch (error) {
      console.error("Error creating event:", error);
    }
    onCloseEventDetailsModal();
    setIsConfirmationModalOpen(false);
  };

  return (
    <DSProvider dsVersion="2.0">
      <EventUpdateConfirmationModal
        isOpen={isConfirmationModalOpen}
        onConfirm={onConfirm}
        onClose={onCloseConfirmationModal}
        title="calendar:delete_event"
        checkboxLabel="calendar:notify_all_the_participants_about_the_deletion_of_this_event"
        confirmationMessage="calendar:delete_confirmation_message"
        saveButtonLabel="common:update"
        cancelButtonLabel="common:cancel"
        height="max-content"
        showWarning={false}
      />
      <RecurrenceConfirmationModal
        actionRef={repeatEventModalActionRef}
        isOpen={false}
        eventTitle={eventModalV2?.eventTitle || ""}
        title={t("calendar:edit_repeat_event")}
        onSave={onSaveRecurrenceHandler}
        onClose={onCloseConfirmationModal}
      />
      <IconButton
        icon={<DeleteOutlined variant={"subtle"} />}
        onClick={clickHandler}
        variant={"neutral"}
        type={"plain"}
        size={"medium"}
        isCompact={true}
        style={style}
        testId={`${testId}-header-delete`}
      />
    </DSProvider>
  );
};

export const DeleteVideoMeetingButton = connect(
  mapStateToProps,
  localMapActionCreators
)(DeleteButton);
